import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { GlobalStateDataContext } from '../../../context-api/data-provider/GlobalStateDataProvider';
import { getKitchensByManagingBranchAPI } from '../../../context-api/api-services/StoreManagement.service';
import { getManagingBranchNameForApi } from '../../../utils/managingBranchName';
import '../../../atomic/inputbox/InputBox.scss';
import './StoreDetailsPage.css';
import { STORE_DETAIL_SECTIONS } from './storeDetailsSections';
import { LAYOUT_MAX_WIDTH } from './storeDetailsConstants';
import { getStoreDetailsCache, setStoreDetailsCache } from './storeDetailsCache';
import { StoreProfileSection } from './sections/StoreProfileSection';
import { AddressSection } from './sections/AddressSection';
import { HoursSection } from './sections/HoursSection';
import { MenuAvailabilitySection } from './sections/MenuAvailabilitySection';
import { DeliverySection } from './sections/DeliverySection';
import { MerchantPackageSection } from './sections/MerchantPackageSection';
import { OnboardingSection } from './sections/OnboardingSection';

async function resolveStoreData(storeId, navState) {
  if (navState && String(navState._id ?? navState.id) === storeId) {
    setStoreDetailsCache(storeId, navState);
    return navState;
  }

  const cached = getStoreDetailsCache(storeId);
  if (cached) return cached;

  const branch = getManagingBranchNameForApi();
  const res = await getKitchensByManagingBranchAPI({
    managing_branch_name: branch,
    page: 1,
    limit: 100,
  });
  if (res.hasError || res.data?.success !== true) return null;

  const list = Array.isArray(res.data?.data) ? res.data.data : [];
  const found = list.find((item) => String(item._id) === storeId) ?? null;
  if (found) setStoreDetailsCache(storeId, found);
  return found;
}

const StoreDetailsPage = () => {
  const { storeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setSelectedMenuTab } = useContext(GlobalStateDataContext);

  const [selectedId, setSelectedId] = useState('profile');
  const [store, setStore] = useState(() => {
    const nav = location.state?.store;
    if (nav && String(nav._id) === storeId) {
      setStoreDetailsCache(storeId, nav);
      return nav;
    }
    return getStoreDetailsCache(storeId);
  });
  const [loading, setLoading] = useState(!store);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const navStore = location.state?.store;
    if (navStore && String(navStore._id) === storeId) {
      setStoreDetailsCache(storeId, navStore);
      setStore(navStore);
      setLoading(false);
      setLoadError(null);
      return undefined;
    }

    const cached = getStoreDetailsCache(storeId);
    if (cached && String(cached._id) === storeId) {
      setStore(cached);
      setLoading(false);
      setLoadError(null);
      return undefined;
    }

    (async () => {
      setLoading(true);
      setLoadError(null);
      const data = await resolveStoreData(storeId, null);
      if (cancelled) return;
      if (!data) {
        setStore(null);
        setLoadError('Could not load store details.');
      } else {
        setStore(data);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [storeId, location.state?.store]);

  const meta = useMemo(
    () => STORE_DETAIL_SECTIONS.find((s) => s.id === selectedId) ?? STORE_DETAIL_SECTIONS[0],
    [selectedId]
  );

  const handleStoreRefresh = async () => {
    const data = await resolveStoreData(storeId, null);
    if (data) {
      setStore(data);
      setStoreDetailsCache(storeId, data);
    }
  };

  const renderPanel = () => {
    if (!store) return null;
    switch (selectedId) {
      case 'profile':
        return <StoreProfileSection meta={meta} store={store} />;
      case 'address':
        return <AddressSection meta={meta} store={store} />;
      case 'hours':
        return <HoursSection meta={meta} store={store} />;
      case 'menu':
        return <MenuAvailabilitySection meta={meta} store={store} />;
      case 'delivery':
        return <DeliverySection meta={meta} store={store} />;
      case 'merchant':
        return <MerchantPackageSection meta={meta} store={store} />;
      case 'onboarding':
        return <OnboardingSection meta={meta} store={store} onStoreRefresh={handleStoreRefresh} />;
      default:
        return <StoreProfileSection meta={meta} store={store} />;
    }
  };

  const goBack = () => {
    setSelectedMenuTab('store-management');
    navigate('/storemanagement');
  };

  if (loading && !store) {
    return (
      <div className="store-details-page">
        <div className="store-details-empty-state">
          <CircularProgress size={28} sx={{ color: '#FF7A28', mb: 2 }} />
          <div>Loading store details…</div>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="store-details-page">
        <div className="store-details-empty-state">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{loadError || 'Store not found'}</div>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            sx={{ textTransform: 'none', color: '#181436' }}
          >
            Back to store management
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="store-details-page">
      <div className="store-details-topbar" style={{ maxWidth: LAYOUT_MAX_WIDTH }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={goBack}
          sx={{
            textTransform: 'none',
            fontFamily: 'Inter Regular, sans-serif',
            color: '#181436',
            px: 0,
          }}
        >
          Back to store management
        </Button>
        <h1 className="store-details-topbar__title">{store.kitchenName}</h1>
        <div className="store-details-topbar__subtitle">Store details · read-only view</div>
      </div>

      <div className="store-details-shell" style={{ maxWidth: LAYOUT_MAX_WIDTH }}>
        <aside className="store-details-toc">
          <div className="store-details-toc-header">Store details</div>
          {STORE_DETAIL_SECTIONS.map((section) => {
            const { Icon } = section;
            const isActive = selectedId === section.id;
            return (
              <div
                key={section.id}
                className={
                  isActive
                    ? 'store-details-toc-link store-details-toc-link--active'
                    : 'store-details-toc-link'
                }
                onClick={() => setSelectedId(section.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setSelectedId(section.id);
                }}
              >
                <Icon sx={{ fontSize: 15, flexShrink: 0, opacity: isActive ? 1 : 0.7 }} />
                {section.title}
              </div>
            );
          })}
        </aside>

        <main className="store-details-content">
          <div className="store-details-form-shell">
            {loading ? (
              <div className="store-details-load-banner">Refreshing store details…</div>
            ) : null}
            <div className="store-details-form-panel">{renderPanel()}</div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StoreDetailsPage;
